// Note: this file is specific to ABT action-creators

import * as reduxApi from 'src/utils/redux-api-bff';
import type {
  ABTEventsSchema,
  JourneysPayload,
  WorkflowsPayload,
  ABTPossibleRelations,
  ChatbotsPayload,
  SurveyV2Payload,
  VoicebotsPayload,
  ExternalLinkV2Payload,
  MeetingTemplatesPayload,
} from 'src/types/audience-actions';
import {
  getRecipientEntityType,
  filterUnSupportedEvents,
} from 'src/utils/audience-actions';
import {getTodayInTimezone} from '@spaced-out/ui-design-system/lib/utils';
import type {Dispatch, ThunkAction, GetState} from 'src/reducers';
import {thunkify as flow} from 'src/utils/thunks';
import {key, fetching} from 'src/utils/redux';


export const RECEIVE_ABT_EVENTS = 'audience-actions/receive-events';
export const RECEIVE_ABT_RELATIONS = 'audience-actions/receive-relations';
export const RECEIVE_JOURNEYS_ACTION = 'audience-actions/receive-journeys';
export const RECEIVE_WORKFLOWS_ACTION = 'audience-actions/receive-workflows';
export const RECEIVE_CHATBOTS_ACTION = 'audience-actions/receive-chatbots';
export const RECEIVE_VOICEBOTS_ACTION = 'audience-actions/receive-voicebots';
export const RECEIVE_SURVEY_V2_ACTION = 'audience-actions/receive-survey-v2';
export const RECEIVE_EXTERNAL_LINK_V2_ACTION =
  'audience-actions/receive-external-link-v2';
export const RECEIVE_MEETING_TEMPLATES_ACTION =
  'audience-actions/receive-meeting-templates';

type ReceiveAbtEventsAction = {
  type: 'audience-actions/receive-events';
  payload: {
    events: ABTEventsSchema;
    sourceLimit: number;
  };
};

type ReceiveAbtRelationsAction = {
  type: 'audience-actions/receive-relations';
  payload: ABTPossibleRelations;
};

type ReceiveJourneysAction = {
  type: 'audience-actions/receive-journeys';
  payload: JourneysPayload;
};

type ReceiveWorkflowsAction = {
  type: 'audience-actions/receive-workflows';
  payload: WorkflowsPayload;
};

type ReceiveChatbotsAction = {
  type: 'audience-actions/receive-chatbots';
  payload: ChatbotsPayload;
};

type ReceiveVoicebotsAction = {
  type: 'audience-actions/receive-voicebots';
  payload: VoicebotsPayload;
};

type ReceiveSurveyV2Action = {
  type: 'audience-actions/receive-survey-v2';
  payload: SurveyV2Payload;
};

type ReceiveExternalLinkV2Action = {
  type: 'audience-actions/receive-external-link-v2';
  payload: ExternalLinkV2Payload;
};

type ReceiveMeetingTemplatesAction = {
  type: 'audience-actions/receive-meeting-templates';
  payload: MeetingTemplatesPayload;
};

export type AudienceABTAction =
  | ReceiveJourneysAction
  | ReceiveWorkflowsAction
  | ReceiveAbtEventsAction
  | ReceiveAbtRelationsAction
  | ReceiveChatbotsAction
  | ReceiveSurveyV2Action
  | ReceiveVoicebotsAction
  | ReceiveExternalLinkV2Action
  | ReceiveMeetingTemplatesAction;

const receiveEvents = (eventsData: {
  events: ABTEventsSchema;
  sourceLimit: number;
}): ReceiveAbtEventsAction => ({
  type: RECEIVE_ABT_EVENTS,
  payload: eventsData,
});

const receiveRelations = (
  relations: ABTPossibleRelations,
): ReceiveAbtRelationsAction => ({
  type: RECEIVE_ABT_RELATIONS,
  payload: relations,
});

const receiveJourneys = (payload: JourneysPayload): ReceiveJourneysAction => ({
  type: RECEIVE_JOURNEYS_ACTION,
  payload,
});

const receiveWorkflows = (
  payload: WorkflowsPayload,
): ReceiveWorkflowsAction => ({
  type: RECEIVE_WORKFLOWS_ACTION,
  payload,
});

const receiveChatbots = (payload: ChatbotsPayload): ReceiveChatbotsAction => ({
  type: RECEIVE_CHATBOTS_ACTION,
  payload,
});

const receiveVoicebots = (
  payload: VoicebotsPayload,
): ReceiveVoicebotsAction => ({
  type: RECEIVE_VOICEBOTS_ACTION,
  payload,
});

const receiveSurveyV2 = (payload: SurveyV2Payload): ReceiveSurveyV2Action => ({
  type: RECEIVE_SURVEY_V2_ACTION,
  payload,
});

const receiveExternalLinkV2 = (
  payload: ExternalLinkV2Payload,
): ReceiveExternalLinkV2Action => ({
  type: RECEIVE_EXTERNAL_LINK_V2_ACTION,
  payload,
});

const receiveMeetingTemplates = (
  payload: MeetingTemplatesPayload,
): ReceiveMeetingTemplatesAction => ({
  type: RECEIVE_MEETING_TEMPLATES_ACTION,
  payload,
});

export const getABTEvents: () => ThunkAction<unknown> = flow(
  key(() => 'action-events'),
  fetching(),
)(
  () => (dispatch: Dispatch) =>
    dispatch(reduxApi.get('action_events')).then((response) => {
      const {action_events, source_limit} = response;
      const actionEvents = filterUnSupportedEvents(action_events);
      return dispatch(
        receiveEvents({
          events: actionEvents,
          sourceLimit: source_limit,
        }),
      );
    }),
);

export const getABTPossibleRelations: (
  entityType: string,
) => ThunkAction<unknown> = flow(
  key((entityType) => `abt-relations/${entityType}`),
  fetching(),
)(
  (entityType) => (dispatch: Dispatch) =>
    dispatch(reduxApi.get(`relations/${entityType}`)).then((response) => {
      dispatch(receiveRelations(response));
    }),
);

export const getActionSourceJourneys: (
  query: {
    abtAttributeName: string;
    abtRelationArray: Array<string>;
    baseEntityType: string;
    abtEntityType: string;
    chatbotIds: Array<string> | null | undefined;
    relations: ABTPossibleRelations;
    fromDate: string;
    toDate: string;
    pageNumber: number;
    pageSize: number;
    queryStr?: string;
    isSurvey?: boolean;
    isExternalLink?: boolean;
  },
  isNew: boolean,
) => ThunkAction<unknown> = flow(
  key((query) => `actionSourceJourneys-${JSON.stringify(query)}`),
  fetching(),
)((query, isNew = true) => async (dispatch: Dispatch) => {
  const {
    pageNumber,
    abtAttributeName,
    abtRelationArray,
    baseEntityType,
    abtEntityType,
    chatbotIds,
    relations,
    fromDate,
    toDate,
    pageSize,
    queryStr,
    isSurvey = false,
    isExternalLink = false,
  } = query;
  const sourceParameter = {
    source_type: 'journey',
    anchor_entity_type: null,
    action_attribute_name: abtAttributeName ?? '',
    action_entity_type: abtEntityType,
    recipient_relation_names: abtRelationArray,
    recipient_entity_type: getRecipientEntityType(
      baseEntityType,
      abtRelationArray,
      relations[abtEntityType],
    ),
    start_date: fromDate ?? '',
    end_date: !toDate || toDate === 'now' ? getTodayInTimezone('') : toDate,
    is_external_link: isExternalLink,
    is_survey: isSurvey,
    chatbot_flow_ids: chatbotIds || [],
    journey_ids: [],
    touchpoint_ids: [],
    query: queryStr,
    offset: (pageNumber - 1) * pageSize,
    limit: pageSize,
  } as const;
  try {
    if (isNew) {
      dispatch(
        receiveJourneys({
          totalCount: 0,
          journeys: [],
          pageNumber,
          isNew,
        }),
      );
    }
    const payload = await dispatch(
      reduxApi.post('j1/abt-source', sourceParameter),
    );
    dispatch(
      receiveJourneys({
        totalCount: payload.total,
        journeys: payload.data,
        pageNumber,
        isNew,
      }),
    );
  } catch {
    dispatch(
      receiveJourneys({
        totalCount: 0,
        journeys: [],
        pageNumber,
        isNew,
      }),
    );
  }
});

export const getActionSourceWorkflows: (
  query: {
    toDate: string;
    fromDate: string;
    relations: ABTPossibleRelations;
    chatbotIds: Array<string>;
    voiceBotIds: Array<string>;
    surveySchemaIds: Array<string>;
    externalLinkV2Ids: Array<string>;
    meetingTemplateIds: Array<string>;
    abtEntityType: string;
    baseEntityType: string;
    abtRelationArray: Array<string>;
    abtAttributeName: string;
    pageSize: number;
    queryStr?: string;
    pageNumber: number;
  },
  isNew: boolean,
) => ThunkAction<unknown> = flow(
  key((query) => `actionSourceJourneys-${JSON.stringify(query)}`),
  fetching(),
)((query, isNew = true) => async (dispatch: Dispatch) => {
  const {
    toDate,
    fromDate,
    relations,
    chatbotIds,
    voiceBotIds,
    surveySchemaIds,
    externalLinkV2Ids,
    meetingTemplateIds,
    abtEntityType,
    baseEntityType,
    abtRelationArray,
    abtAttributeName,
    pageSize,
    queryStr,
    pageNumber,
  } = query;
  const endDate = !toDate || toDate === 'now' ? getTodayInTimezone('') : toDate;
  const isVoiceV2 = abtEntityType === 'action_voice_ai_v2';
  const sourceParameter = {
    source_type: 'workflow',
    action_entity_type: abtEntityType,
    anchor_entity_type: null,
    action_attribute_name: abtAttributeName || '',
    recipient_relation_names: abtRelationArray,
    recipient_entity_type: getRecipientEntityType(
      baseEntityType,
      abtRelationArray,
      relations[abtEntityType],
    ),
    end_date: endDate,
    start_date: fromDate || '',
    chatbot_flow_ids: chatbotIds || [],
    voice_bot_ids: isVoiceV2 ? [] : voiceBotIds || [],
    voice_bot_v2_ids: isVoiceV2 ? voiceBotIds || [] : [],
    survey_schema_ids: surveySchemaIds || [],
    cipher_hash_urls: externalLinkV2Ids || [],
    meeting_template_ids: meetingTemplateIds || [],
    workflow_ids: [],
    node_ids: [],
    query: queryStr,
    offset: (pageNumber - 1) * pageSize,
    limit: pageSize,
  } as const;
  try {
    if (isNew) {
      dispatch(
        receiveWorkflows({
          totalCount: 0,
          workflows: [],
          pageNumber,
          isNew,
        }),
      );
    }
    const payload = await dispatch(
      reduxApi.post('j2/abt-source', sourceParameter),
    );
    dispatch(
      receiveWorkflows({
        totalCount: payload.total,
        workflows: payload.data,
        pageNumber,
        isNew,
      }),
    );
  } catch {
    dispatch(
      receiveWorkflows({
        totalCount: 0,
        workflows: [],
        pageNumber,
        isNew,
      }),
    );
  }
});

export const getJourneySourceDetails: (query: {
  abtAttributeName: string;
  abtRelationArray: Array<string>;
  isExternalLink: boolean;
  baseEntityType: string;
  abtEntityType: string;
  touchpointIds: Array<string>;
  journeyIds: Array<string>;
  chatbotIds: Array<string> | null | undefined;
  fromDate: string | null | undefined;
  isSurvey?: boolean;
  toDate: string | null | undefined;
}) => ThunkAction<unknown> =
  (query) => async (dispatch: Dispatch, getState: GetState) => {
    const {relations} = getState().audienceActions;
    const {
      abtAttributeName,
      abtRelationArray,
      isExternalLink = false,
      baseEntityType,
      abtEntityType,
      touchpointIds,
      journeyIds,
      chatbotIds,
      fromDate,
      isSurvey = false,
      toDate,
    } = query;
    const sourceParameter = {
      source_type: 'journey',
      anchor_entity_type: null,
      action_attribute_name: abtAttributeName ?? '',
      action_entity_type: abtEntityType,
      recipient_relation_names: abtRelationArray,
      recipient_entity_type: getRecipientEntityType(
        baseEntityType,
        abtRelationArray,
        relations[abtEntityType],
      ),
      start_date: fromDate ?? getTodayInTimezone(''),
      end_date: !toDate || toDate === 'now' ? getTodayInTimezone('') : toDate,
      is_external_link: isExternalLink,
      is_survey: isSurvey,
      chatbot_flow_ids: chatbotIds || [],
      journey_ids: journeyIds,
      touchpoint_ids: touchpointIds,
      query: '',
      offset: 0,
      limit: journeyIds.length,
    } as const;
    return dispatch(reduxApi.post('j1/abt-source', sourceParameter));
  };

export const getWorkflowSourceDetails: (query: {
  abtRelationArray: Array<string>;
  abtAttributeName: string;
  baseEntityType: string;
  abtEntityType: string;
  workflowIds: Array<string>;
  surveySchemaIds: Array<string>;
  meetingTemplateIds: Array<string>;
  externalLinkV2Ids: Array<string>;
  voiceBotIds: Array<string>;
  chatbotIds: Array<string>;
  fromDate: string | null | undefined;
  nodeIds: Array<string>;
  toDate: string | null | undefined;
}) => ThunkAction<unknown> =
  (query) => async (dispatch: Dispatch, getState: GetState) => {
    const {relations} = getState().audienceActions;
    const {
      abtAttributeName,
      abtRelationArray,
      baseEntityType,
      abtEntityType,
      workflowIds,
      surveySchemaIds,
      meetingTemplateIds,
      externalLinkV2Ids,
      voiceBotIds,
      chatbotIds,
      nodeIds,
      fromDate,
      toDate,
    } = query;

    const sourceParameter = {
      source_type: 'workflow',
      anchor_entity_type: null,
      action_attribute_name: abtAttributeName ?? '',
      action_entity_type: abtEntityType,
      recipient_relation_names: abtRelationArray,
      recipient_entity_type: getRecipientEntityType(
        baseEntityType,
        abtRelationArray,
        relations[abtEntityType],
      ),
      start_date: fromDate ?? getTodayInTimezone(''),
      end_date: !toDate || toDate === 'now' ? getTodayInTimezone('') : toDate,
      voice_bot_ids:
        abtEntityType === 'action_voice_ai_v2' ? [] : voiceBotIds || [],
      voice_bot_v2_ids:
        abtEntityType === 'action_voice_ai_v2' ? voiceBotIds || [] : [],
      chatbot_flow_ids: chatbotIds || [],
      survey_schema_ids: surveySchemaIds || [],
      meeting_template_ids: meetingTemplateIds || [],
      cipher_hash_urls: externalLinkV2Ids || [],
      workflow_ids: workflowIds,
      node_ids: nodeIds,
      query: '',
      offset: 0,
      limit: workflowIds.length,
    } as const;

    return dispatch(reduxApi.post('j2/abt-source', sourceParameter));
  };

export const getActionSourceChatbots: (
  query: {
    baseEntityType: string;
    fromDate: string;
    toDate: string;
    pageNumber: number;
    pageSize: number;
    queryStr?: string;
    useCases?: Array<string>;
  },
  isNew: boolean,
) => ThunkAction<unknown> = flow(
  key((query) => `actionSourceChatbots-${JSON.stringify(query)}`),
  fetching(),
)((query, isNew = true) => async (dispatch: Dispatch) => {
  const {
    pageNumber,
    baseEntityType,
    fromDate,
    toDate,
    pageSize,
    queryStr,
    useCases,
  } = query;
  const sourceParameter = {
    anchor_entity_type: baseEntityType,
    start_date: fromDate ?? '',
    end_date: !toDate || toDate === 'now' ? getTodayInTimezone('') : toDate,
    flow_ids: [],
    name: queryStr,
    use_cases: useCases,
    offset: (pageNumber - 1) * pageSize,
    limit: pageSize,
  } as const;
  try {
    if (isNew) {
      dispatch(
        receiveChatbots({
          totalCount: 0,
          chatbots: [],
          pageNumber,
          isNew,
        }),
      );
    }
    const payload = await dispatch(
      reduxApi.post('chatbot/abt-source', sourceParameter),
    );
    dispatch(
      receiveChatbots({
        totalCount: payload.total,
        chatbots: payload.data,
        pageNumber,
        isNew,
      }),
    );
  } catch {
    dispatch(
      receiveChatbots({
        totalCount: 0,
        chatbots: [],
        pageNumber,
        isNew,
      }),
    );
  }
});

export const getActionSourceVoicebots: (
  query: {
    baseEntityType: string;
    pageNumber: number;
    queryStr?: string;
    pageSize: number;
    fromDate: string;
    toDate: string;
    isInbound?: boolean;
    isV2?: boolean;
  },
  isNew: boolean,
) => ThunkAction<unknown> = flow(
  key((query) => `actionSourceVoicebots-${JSON.stringify(query)}`),
  fetching(),
)((query, isNew = true) => async (dispatch: Dispatch) => {
  const {
    baseEntityType,
    pageNumber,
    pageSize,
    fromDate,
    queryStr,
    toDate,
    isInbound = false,
    isV2 = false,
  } = query;
  const endpoint = isInbound
    ? 'voice-inbound-bot/abt-source'
    : isV2
    ? 'voicebot-v2/abt-source'
    : 'voicebot/abt-source';
  const sourceParameter = {
    anchor_entity_type: baseEntityType,
    voice_bot_ids: [],
    start_date: fromDate ?? '',
    end_date: !toDate || toDate === 'now' ? getTodayInTimezone('') : toDate,
    offset: (pageNumber - 1) * pageSize,
    limit: pageSize,
    name: queryStr,
  } as const;
  try {
    if (isNew) {
      dispatch(
        receiveVoicebots({
          totalCount: 0,
          voicebots: [],
          pageNumber,
          isNew,
        }),
      );
    }
    const payload = await dispatch(reduxApi.post(endpoint, sourceParameter));
    dispatch(
      receiveVoicebots({
        totalCount: payload.total,
        voicebots: payload.data,
        pageNumber,
        isNew,
      }),
    );
  } catch {
    dispatch(
      receiveVoicebots({
        totalCount: 0,
        voicebots: [],
        pageNumber,
        isNew,
      }),
    );
  }
});

export const getActionSourceSurveyV2: (
  query: {
    baseEntityType: string;
    pageNumber: number;
    queryStr?: string;
    pageSize: number;
    fromDate: string;
    toDate: string;
  },
  isNew: boolean,
) => ThunkAction<unknown> = flow(
  key((query) => `actionSourceSurveyV2-${JSON.stringify(query)}`),
  fetching(),
)((query, isNew = true) => async (dispatch: Dispatch) => {
  const {baseEntityType, pageNumber, pageSize, fromDate, toDate, queryStr} =
    query;
  const sourceParameter = {
    anchor_entity_type: baseEntityType,
    survey_schema_ids: [],
    start_date: fromDate ?? '',
    end_date: !toDate || toDate === 'now' ? getTodayInTimezone('') : toDate,
    offset: (pageNumber - 1) * pageSize,
    limit: pageSize,
    name: queryStr,
  } as const;
  try {
    if (isNew) {
      dispatch(
        receiveSurveyV2({
          totalCount: 0,
          surveyV2: [],
          pageNumber,
          isNew,
        }),
      );
    }
    const payload = await dispatch(
      reduxApi.post('surveyV2/abt-source', sourceParameter),
    );
    dispatch(
      receiveSurveyV2({
        totalCount: payload.total,
        surveyV2: payload.data,
        pageNumber,
        isNew,
      }),
    );
  } catch {
    dispatch(
      receiveSurveyV2({
        totalCount: 0,
        surveyV2: [],
        pageNumber,
        isNew,
      }),
    );
  }
});

export const getActionSourceExternalLinkV2: (
  query: {
    toDate: string;
    pageSize: number;
    fromDate: string;
    queryStr?: string;
    pageNumber: number;
    baseEntityType: string;
    abtEntityType?: string;
    abtRelationArray?: Array<string>;
  },
  isNew: boolean,
) => ThunkAction<unknown> = flow(
  key((query) => `actionSourceExternalLinkV2-${JSON.stringify(query)}`),
  fetching(),
)((query, isNew = true) => async (dispatch: Dispatch, getState: GetState) => {
  const {
    baseEntityType,
    pageNumber,
    pageSize,
    fromDate,
    toDate,
    queryStr,
    abtEntityType = '',
    abtRelationArray = [],
  } = query;
  const {relations} = getState().audienceActions;
  const recipientEntityType = getRecipientEntityType(
    baseEntityType,
    abtRelationArray,
    relations[abtEntityType],
  );
  const sourceParameter = {
    anchor_entity_type: recipientEntityType || baseEntityType,
    start_date: fromDate ?? '',
    hash_urls: [],
    end_date: !toDate || toDate === 'now' ? getTodayInTimezone('') : toDate,
    offset: (pageNumber - 1) * pageSize,
    limit: pageSize,
    name: queryStr,
  } as const;
  try {
    if (isNew) {
      dispatch(
        receiveExternalLinkV2({
          externalLinkV2: [],
          totalCount: 0,
          pageNumber,
          isNew,
        }),
      );
    }
    const payload = await dispatch(
      reduxApi.post('cipherExternalLink/abt-source', sourceParameter),
    );
    dispatch(
      receiveExternalLinkV2({
        externalLinkV2: payload.data,
        totalCount: payload.total,
        pageNumber,
        isNew,
      }),
    );
  } catch {
    dispatch(
      receiveExternalLinkV2({
        externalLinkV2: [],
        totalCount: 0,
        pageNumber,
        isNew,
      }),
    );
  }
});

export const getActionSourceMeetingTemplates: (
  query: {
    baseEntityType: string;
    pageNumber: number;
    queryStr?: string;
    pageSize: number;
    fromDate: string;
    toDate: string;
  },
  isNew: boolean,
) => ThunkAction<unknown> = flow(
  key((query) => `actionSourceMeetingTemplates-${JSON.stringify(query)}`),
  fetching(),
)((query, isNew = true) => async (dispatch: Dispatch) => {
  const {baseEntityType, pageNumber, pageSize, fromDate, queryStr, toDate} =
    query;
  const sourceParameter = {
    anchor_entity_type: baseEntityType,
    meeting_template_ids: [],
    start_date: fromDate ?? '',
    end_date: !toDate || toDate === 'now' ? getTodayInTimezone('') : toDate,
    offset: (pageNumber - 1) * pageSize,
    limit: pageSize,
    name: queryStr,
  } as const;
  try {
    if (isNew) {
      dispatch(
        receiveMeetingTemplates({
          totalCount: 0,
          meetingTemplates: [],
          pageNumber,
          isNew,
        }),
      );
    }
    const payload = await dispatch(
      reduxApi.post('meetingTemplate/abt-source', sourceParameter),
    );
    dispatch(
      receiveMeetingTemplates({
        totalCount: payload.total,
        meetingTemplates: payload.data,
        pageNumber,
        isNew,
      }),
    );
  } catch {
    dispatch(
      receiveMeetingTemplates({
        totalCount: 0,
        meetingTemplates: [],
        pageNumber,
        isNew,
      }),
    );
  }
});

export const getChatbotSourceDetails: (query: {
  baseEntityType: string;
  chatbotIds: Array<string>;
  fromDate: string | null | undefined;
  toDate: string | null | undefined;
}) => ThunkAction<unknown> = (query) => async (dispatch: Dispatch) => {
  const {baseEntityType, chatbotIds, fromDate, toDate} = query;
  const sourceParameter = {
    anchor_entity_type: baseEntityType,
    start_date: fromDate ?? getTodayInTimezone(''),
    end_date: !toDate || toDate === 'now' ? getTodayInTimezone('') : toDate,
    flow_ids: chatbotIds,
    use_cases: [],
    name: '',
    offset: 0,
    limit: chatbotIds.length,
  } as const;
  return dispatch(reduxApi.post('chatbot/abt-source', sourceParameter));
};

export const getVoicebotSourceDetails: (query: {
  baseEntityType: string;
  voiceBotIds: Array<string>;
  fromDate: string | null | undefined;
  toDate: string | null | undefined;
  isInbound?: boolean;
  isV2?: boolean;
}) => ThunkAction<unknown> = (query) => async (dispatch: Dispatch) => {
  const {
    baseEntityType,
    voiceBotIds,
    fromDate,
    toDate,
    isInbound = false,
    isV2 = false,
  } = query;
  const endpoint = isInbound
    ? 'voice-inbound-bot/abt-source'
    : isV2
    ? 'voicebot-v2/abt-source'
    : 'voicebot/abt-source';
  const sourceParameter = {
    anchor_entity_type: baseEntityType,
    start_date: fromDate ?? getTodayInTimezone(''),
    end_date: !toDate || toDate === 'now' ? getTodayInTimezone('') : toDate,
    voice_bot_ids: voiceBotIds,
    use_cases: [],
    name: '',
    offset: 0,
    limit: voiceBotIds.length,
  } as const;
  return dispatch(reduxApi.post(endpoint, sourceParameter));
};

export const getMeetingTemplateSourceDetails: (query: {
  meetingTemplateIds: Array<string>;
  baseEntityType: string;
  fromDate: string | null | undefined;
  toDate: string | null | undefined;
}) => ThunkAction<unknown> = (query) => async (dispatch: Dispatch) => {
  const {baseEntityType, meetingTemplateIds, fromDate, toDate} = query;
  const sourceParameter = {
    anchor_entity_type: baseEntityType,
    start_date: fromDate ?? getTodayInTimezone(''),
    end_date: !toDate || toDate === 'now' ? getTodayInTimezone('') : toDate,
    meeting_template_ids: meetingTemplateIds,
    use_cases: [],
    name: '',
    offset: 0,
    limit: meetingTemplateIds.length,
  } as const;
  return dispatch(reduxApi.post('meetingTemplate/abt-source', sourceParameter));
};

export const getSurveyV2SourceDetails: (query: {
  surveySchemaIds: Array<string>;
  baseEntityType: string;
  fromDate: string | null | undefined;
  toDate: string | null | undefined;
}) => ThunkAction<unknown> = (query) => async (dispatch: Dispatch) => {
  const {baseEntityType, surveySchemaIds, fromDate, toDate} = query;
  const sourceParameter = {
    anchor_entity_type: baseEntityType,
    start_date: fromDate ?? getTodayInTimezone(''),
    end_date: !toDate || toDate === 'now' ? getTodayInTimezone('') : toDate,
    survey_schema_ids: surveySchemaIds,
    use_cases: [],
    name: '',
    offset: 0,
    limit: surveySchemaIds.length,
  } as const;
  return dispatch(reduxApi.post('surveyV2/abt-source', sourceParameter));
};

export const getExternalLinkV2SourceDetails: (query: {
  externalLinkV2Ids: Array<string>;
  baseEntityType: string;
  abtEntityType?: string;
  abtRelationArray?: Array<string>;
  fromDate: string | null | undefined;
  toDate: string | null | undefined;
}) => ThunkAction<unknown> =
  (query) => async (dispatch: Dispatch, getState: GetState) => {
    const {relations} = getState().audienceActions;
    const {
      baseEntityType,
      abtEntityType = '',
      abtRelationArray = [],
      externalLinkV2Ids,
      fromDate,
      toDate,
    } = query;
    const recipientEntityType = getRecipientEntityType(
      baseEntityType,
      abtRelationArray,
      relations[abtEntityType],
    );
    const sourceParameter = {
      anchor_entity_type: recipientEntityType || baseEntityType,
      start_date: fromDate ?? getTodayInTimezone(''),
      hash_urls: externalLinkV2Ids,
      end_date: !toDate || toDate === 'now' ? getTodayInTimezone('') : toDate,
      offset: 0,
      limit: externalLinkV2Ids.length,
      name: '',
    } as const;
    return dispatch(
      reduxApi.post('cipherExternalLink/abt-source', sourceParameter),
    );
  };
